<script>
import { authComputed } from '@state/helpers'
import moment from 'moment/moment'
import { readNotification } from '@src/api/notifications'
import { isMobileMixin } from '@src/mixins/isMobileMixin'
import _ from 'lodash'

export default {
  mixins: [isMobileMixin],
  props: {
    coursesNotifications: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return { selectedIndex: 0, viewedNotifications: new Set() }
  },
  computed: {
    ...authComputed,
    selectedCourseExtras() {
      const extras = this.coursesNotifications[this.selectedIndex]?.extras ?? '{}'
      return JSON.parse(extras)
    },
    selectedCourse() {
      return this.coursesNotifications[this.selectedIndex] ?? null
    },
    courseCreationDate() {
      const creationDate = this.selectedCourseExtras.course_creation_date
      return creationDate ? moment(creationDate, 'YYYY-MM-DD HH:mm:ss').format('MM/DD/YYYY') : ''
    },
  },
  watch: {
    coursesNotifications: {
      immediate: true,
      handler(newVal) {
        if (newVal.length > 0) {
          this.viewNotification()
        }
      },
    },
  },
  methods: {
    goToNext() {
      this.selectedIndex = (this.selectedIndex + 1) % this.coursesNotifications.length
      this.viewNotification()
    },
    goToPrevious() {
      this.selectedIndex =
        (this.selectedIndex - 1 + this.coursesNotifications.length) % this.coursesNotifications.length
      this.viewNotification()
    },
    markViewedNotificationsAsRead() {
      this.viewedNotifications.forEach((notificationId) => {
        readNotification({ id: notificationId })
      })
    },
    viewNotification() {
      if (this.selectedCourse) {
        this.viewedNotifications.add(this.selectedCourse.id)
      }
    },
    launchApp(appInfo) {
      const url = appInfo.actionLink
        ? appInfo.actionLink
        : `https://mottomortgage.docebosaas.com/learn/courses/${appInfo.course_id}/${_.kebabCase(
            appInfo.course_name
          )}/lessons`
      window.open(url, '_blank')
    },
  },
}
</script>

<template>
  <b-modal
    id="course-reminder-modal"
    :modal-class="['right', 'modal-resource']"
    hide-footer
    :hide-header="!isMobile"
    :visible="!!coursesNotifications.length"
    @hide="markViewedNotificationsAsRead">
    <div :class="$style.container">
      <div v-if="coursesNotifications.length > 1" :class="$style.buttons">
        <div @click="goToPrevious">
          <MottoIcon :class="$style.previousButton" name="Arrow" icon-color="orange" class="cursor-pointer" />
          <p>Previous</p>
        </div>
        <div @click="goToNext">
          <p>Next</p>
          <MottoIcon :class="$style.nextButton" name="Arrow" icon-color="orange" class="cursor-pointer" />
        </div>
      </div>
      <div v-else style="height: 40px"></div>
      <div :class="$style.headerContainer">
        <h2
          >{{ selectedCourseExtras.remindedBy }} is requesting that you complete the following course in MottoLearn.</h2
        >
        <p
          >Please <u @click="launchApp(selectedCourseExtras)">launch</u> the MottoLearn app below to complete this
          request</p
        >
      </div>
      <div :class="$style.courseInfoContainer">
        <div :class="$style.infoContainer">
          <div :class="$style.thumbnailContainer">
            <div>
              <img :src="selectedCourseExtras.thumbnail" alt="Course Thumbnail" :class="$style.courseThumbnail" />
            </div>
            <h3>{{ selectedCourseExtras.course_name }}</h3>
          </div>
          <div>
            <div :class="$style.infoGroupContainer">
              <div :class="$style.infoGroup">
                <strong>Created By</strong>
                <p>{{ selectedCourseExtras.course_creation_by_name }}</p>
              </div>
              <div :class="$style.infoGroup">
                <strong>Creation Date</strong>
                <p>{{ courseCreationDate }}</p>
              </div>
            </div>
            <div :class="$style.infoGroupContainer">
              <div :class="$style.infoGroup">
                <strong>Description</strong>
                <div v-html="selectedCourseExtras.description"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div :class="$style.learnIcons">
        <div>
          <MottoIcon name="RocketColored"></MottoIcon>
          <p
            class="color-secondary-light-blue cursor-pointer"
            style="text-decoration: underline"
            @click="launchApp(selectedCourseExtras)"
            >> Launch App</p
          ></div
        >
      </div>
    </div>
  </b-modal>
</template>

<style lang="scss" module>
@import '@design';

.container {
  @media (max-width: $lg-breakpoint) {
    width: 100vw;
    min-width: 100vw;
  }

  display: flex;
  flex-direction: column;
  max-width: 600px;
  min-height: 100%;
}

.previousButton {
  > svg {
    width: 30px;
    height: 30px;
  }
}

.nextButton {
  > svg {
    width: 30px;
    height: 30px;
    transform: rotate(180deg);
  }
}

.buttons {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;

  > div {
    display: flex;
    align-items: center;
    cursor: pointer;
    gap: 5px;

    p {
      font-size: 16px;
      font-weight: bold;
      color: $secondary-orange;
    }
  }
}

.headerContainer {
  @media (max-width: $lg-breakpoint) {
    width: calc(100% - 40px);
    padding: 8px;
    margin: 0 0 0 18px;
    text-align: center;
  }

  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 95%;
  padding: 20px 0;
  margin: 0 0 5px 24px;
  overflow: hidden;
  background-color: $secondary-orange;
  border-radius: 8px;

  h2 {
    z-index: 1;
    font-size: 20px;
    font-weight: normal;
    color: #000;
    text-align: center;
  }

  p {
    z-index: 1;
    font-size: 16px;
    color: #000;
  }

  u {
    cursor: pointer;
  }
}

.descriptionsContainer {
  margin: 15px 50px 0;
  color: $primary-blue;
}

.courseInfoContainer {
  @media (max-width: $lg-breakpoint) {
    flex-direction: column;
  }

  display: flex;
  width: 80%;
  margin: 10px auto 0;
}

.thumbnailContainer {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.courseThumbnail {
  max-width: 230px;
  margin-right: 10px;
  border-radius: 8px;
}

.learnIcons {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  align-items: flex-start;
  max-height: 55px;
  margin: auto 30px 30px;
  background: $lightest-gray;
  border-radius: 8px;

  div {
    display: flex;
    align-items: center;
    margin: 5px 15px;
  }

  svg {
    width: 30px;
    height: 30px;
  }

  p {
    font-size: 16px;
    font-weight: bold;
  }
}

.infoContainer {
  padding: 2px 2px 8px;
  background: $white;
  border-radius: 8px;

  h3 {
    @media (max-width: $lg-breakpoint) {
      margin-top: 10px;
      line-height: normal;
    }

    margin: 0 0 0 6px;
    font-size: 18px;
    line-height: 55px;
  }
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px;
  text-align: left;
  background: $primary-blue;
  border-radius: 8px;

  .profilePic {
    flex-shrink: 0;
    margin-left: 10px;
  }

  h4 {
    margin-bottom: 0;
    font-weight: bold;
    color: $white;
  }

  p {
    font-size: 12px;
    color: $white;
  }
}

.infoGroupContainer {
  display: flex;
  justify-content: space-between;
  padding: 8px;
  gap: 10px;
}

.infoGroup {
  strong {
    display: block;
    color: $secondary-light-blue;
    text-align: left;
  }

  p {
    color: $dark-gray;
  }
}

.courseTooltip {
  div[class='tooltip-inner'] {
    width: 300px;
    max-width: 270px;
    padding: 2px;
  }
}
</style>
